import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { useSelector } from 'react-redux';

import {
  changeGeoMapUnMatched,
  editChartPropItem,
} from '../../../redux/ChartPoperties/ChartPropertiesActions';
import Logger from '../../../Logger';
import { useDispatch } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import {
  updateCardControls,
  updateExtraMeasureField,
} from '../../../redux/ChartPoperties/ChartControlsActions';
import { callQueryAPI } from '../../../redux/ChartPoperties/ChartControlsActions';
import { RootState } from '../../../redux';

interface RenameFunctionProps {
  renamefn: boolean;
  setRenamefn: React.Dispatch<React.SetStateAction<boolean>>;
  propKey: string;
  bIndex: number;
  itemIndex: number;
  tooltipFormats?: any;
}

const RenameFunction = ({
  renamefn,
  setRenamefn,
  propKey,
  bIndex,
  itemIndex,
  tooltipFormats,
}: RenameFunctionProps) => {
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  const { chartProp, chartControls } = useSelector((state: RootState) => ({
    chartProp: state.chartProperties,
    chartControls: state.chartControls,
  }));
  const cardSubText = chartControls.properties[propKey]?.cardControls?.subText || '';
  const isSimpleCard = chartProp.properties[propKey].chartType === 'simplecard';
  let currentChartAxesName = 'chartAxes';

  const field = tooltipFormats
    ? tooltipFormats?.extraMeasureField[bIndex]?.fields[itemIndex]
    : chartProp.properties[propKey].chartAxes[bIndex].fields[itemIndex] || {};

  // Initialize renameText with the current name_with_aggr value
  const Aggname = `${field.displayname}`;
  const initialRenameText =
    field.agg && field.agg.trim() !== '' && !field.isTextRenamed ? Aggname : field.displayname;

  const [renameText, setRenameText] = useState(initialRenameText || '');
  const [error, setError] = useState('');
  const [isManualInput, setIsManualInput] = useState(false); // Track if the value was manually entered

  Logger('info', '**initialRenameText****', initialRenameText);

  useEffect(() => {
    if (renamefn && !isManualInput) {
      setRenameText(
        field.agg && field.agg.trim() !== '' && !field.isTextRenamed ? Aggname : field.displayname,
      );

      setError(''); // Clear any previous error when renaming starts
    }
  }, [renamefn, field.agg, field.displayname, isManualInput]);

  useEffect(() => {
    if (isSimpleCard && renamefn && !isManualInput) {
      setRenameText(cardSubText);
    }
  }, [cardSubText, renamefn, isManualInput, isSimpleCard]);

  // Get all fieldnames except the current field's fieldname

  const chartAxesFieldNames = chartProp.properties[propKey].chartAxes.flatMap((axis: any) =>
    axis.fields.map((field: any) => field.displayname),
  );

  // Get all fieldnames from extra measures
  const extraMeasureFieldNames = tooltipFormats?.extraMeasureField
    ? tooltipFormats.extraMeasureField.flatMap((measure: any) =>
        measure.fields.map((field: any) => field.displayname),
      )
    : [];

  const allFieldNames = [...chartAxesFieldNames, ...extraMeasureFieldNames];
  const otherFieldNames = allFieldNames.filter((name: string) => name !== field.displayname);

  const handleRenameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newText = event.target.value;
    setIsManualInput(true);
    setRenameText(newText);

    // Check if the new text conflicts with other field names
    if (newText.trim() !== '' && otherFieldNames.includes(newText.trim())) {
      setError('Fieldname already taken!');
    } else if (newText.trim() === '') {
      setError('Input some value!');
    } else {
      setError(''); // Clear the error if the new text is valid
    }
  };

  const handleOkClick = () => {
    // Validate if the new name is not already in the fieldnames list
    if (otherFieldNames.includes(renameText.trim())) {
      setError('Fieldname already taken!');
    } else if (renameText.trim() === '') {
      setError('Input some value!');
    } else {
      setIsManualInput(false);
      if (isSimpleCard) {
        dispatch(updateCardControls(propKey, 'generateLabelName', 'Manual'));
      }
      const displayCopy = {
        ...field,
        displayname: renameText,
        isTextRenamed: true,
        Aggname: Aggname,
      }; // Update the displayname with the entered text
      if (tooltipFormats) {
        dispatch(updateExtraMeasureField(propKey, bIndex, itemIndex, displayCopy));
      } else {
        if (
          chartProp.properties[propKey].chartType === 'filledMap' ||
          chartProp.properties[propKey].chartType === 'bubbleMap'
        ) {
          const data = chartProp.properties[propKey].Geo.unMatchedChartData || [];
          let entryIndex = 0;
          if (
            (chartProp.properties[propKey].chartType === 'filledMap' && bIndex === 2) ||
            bIndex === 3
          ) {
            entryIndex = 1;
          }
          const updatedData = data.map((item: any) => {
            const entries = Object.entries(item);
            entries[entryIndex][0] = renameText; // new displayname
            return Object.fromEntries(entries);
          });
          dispatch(changeGeoMapUnMatched(propKey, updatedData)); // updating unmatched map data with new displayname
        }
        // Update Redux store with the new displayname
        dispatch(
          editChartPropItem('updateQuery', {
            propKey,
            binIndex: bIndex,
            itemIndex,
            item: displayCopy,
            currentChartAxesName,
          }),
        );
      }

      if (isSimpleCard) {
        dispatch(updateCardControls(propKey, 'subText', renameText));
      }
      dispatch(callQueryAPI(propKey, true));
      setRenamefn(false); // Close the rename menu
    }
  };

  const handleCancelClick = () => {
    setIsManualInput(false);
    setRenamefn(false);
  };

  return (
    <div>
      <Dialog
        open={renamefn}
        onClose={() => setRenamefn(false)}
        PaperProps={{
          sx: {
            textAlign: 'center',
          },
        }}
      >
        <DialogTitle>
          <Typography
            variant="h5"
            sx={{ padding: 'px', color: '#b6b6b6', marginBottom: '20px', marginTop: '10px' }}
          >
            Rename for Visual
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              marginBottom: '10px',
            }}
          >
            <TextField
              value={renameText}
              onChange={handleRenameChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && renameText.trim() !== '') {
                  handleOkClick();
                }
              }}
              placeholder="Rename for Visual"
              size="small"
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#b6b6b6',
                  },
                  '&:hover fieldset': {
                    borderColor: '#b6b6b6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#b6b6b6',
                  },
                  '& input': {
                    backgroundColor: 'transparent !important',
                  },
                  '&:hover input': {
                    backgroundColor: 'transparent !important',
                  },
                  '&.Mui-focused input': {
                    backgroundColor: 'transparent !important',
                  },

                  '&:hover': {
                    backgroundColor: 'transparent !important',
                  },
                  '&.Mui-focusVisible': {
                    backgroundColor: 'transparent !important',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'transparent !important',
                  },
                },
              }}
            />

            {error && <Typography color="error">{error}</Typography>}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                mt: 2,
              }}
            >
              <Button
                onClick={handleCancelClick}
                sx={{
                  fontSize: '10px',
                  color: '#b6b6b6',
                  border: '2px solid  #b6b6b6',
                  borderRadius: '2px',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    boxShadow: '0px 0px 2px 1px  #b6b6b6',
                  },
                  '&:focus': {
                    backgroundColor: 'transparent',
                    boxShadow: '0px 0px 2px 1px #b6b6b6',
                  },
                }}
              >
                CANCEL
              </Button>

              <Button
                onClick={handleOkClick}
                disabled={renameText.trim() === '' || otherFieldNames.includes(renameText.trim())}
                sx={{
                  fontSize: '10px',
                  color: '#2bb9bb',
                  border:
                    renameText.trim() === '' || otherFieldNames.includes(renameText.trim())
                      ? '2px solid #b6b6b6'
                      : '2px solid #2bb9bb',
                  borderRadius: '2px',
                  // textTransform: "initial",
                  '&:hover': {
                    backgroundColor: 'transparent',
                    boxShadow: '0px 0px 2px 1px #2bb9bb',
                  },
                  '&:focus': {
                    backgroundColor: 'transparent',
                    boxShadow: '0px 0px 2px 1px #2bb9bb',
                  },
                }}
              >
                OK
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RenameFunction;
